import { TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PlusIcon, TrashIcon } from '@/components/icons';
import type { ToolPermissionPreHookAction } from '@/api/agent.api';
import type { AiApprovalFormState, ToolPermissionPreHookFormState } from './agentFormTypes';
import {
  EMPTY_TOOL_PRE_HOOK,
  TOOL_PRE_HOOK_ACTION_OPTIONS,
  TOOL_PRE_HOOK_NO_AUTO_APPROVE_OPTIONS,
  toolPreHookActionLabel,
} from './toolPreHooks';

interface Props {
  value: ToolPermissionPreHookFormState[];
  onChange: (v: ToolPermissionPreHookFormState[]) => void;
}

function ActionSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: ToolPermissionPreHookAction | '';
  options: { value: ToolPermissionPreHookAction; label: string }[];
  onChange: (v: ToolPermissionPreHookAction) => void;
}) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">
        {label} <span className="text-destructive">*</span>
      </Label>
      <Select value={value} onValueChange={(v) => onChange(v as ToolPermissionPreHookAction)}>
        <SelectTrigger className="h-9 text-xs">
          <SelectValue placeholder="请选择">{toolPreHookActionLabel(value)}</SelectValue>
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ToolMatchField({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">
        适用工具 <span className="text-destructive">*</span>
      </Label>
      <Input
        placeholder="exec、file_editor 等，多个用逗号分隔（不支持 *）"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 font-mono text-xs"
      />
    </div>
  );
}

/** 超时与重试次数；0 表示使用 framework 默认值 */
function LimitFields({
  timeout,
  retries,
  timeoutPlaceholder,
  retriesLabel,
  onChange,
}: {
  timeout: number;
  retries: number;
  timeoutPlaceholder: string;
  retriesLabel: string;
  onChange: (patch: { timeout?: number; retries?: number }) => void;
}) {
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">超时（秒）</Label>
        <Input
          type="number"
          min={0}
          step="0.1"
          placeholder={timeoutPlaceholder}
          value={timeout || ''}
          onChange={(e) => onChange({ timeout: parseFloat(e.target.value) || 0 })}
          className="h-9 text-xs"
        />
      </div>
      <div className="space-y-1.5">
        <Label className="text-xs text-muted-foreground">{retriesLabel}</Label>
        <Input
          type="number"
          min={0}
          step={1}
          placeholder="默认 1"
          value={retries || ''}
          onChange={(e) => onChange({ retries: parseInt(e.target.value, 10) || 0 })}
          className="h-9 text-xs"
        />
      </div>
    </div>
  );
}

export default function ToolPreHooksFields({ value, onChange }: Props) {
  const update = (index: number, patch: Partial<ToolPermissionPreHookFormState>) =>
    onChange(value.map((hook, i) => (i === index ? { ...hook, ...patch } : hook)));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium text-primary">前置钩子 (pre-hook)</h4>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => onChange([...value, { ...EMPTY_TOOL_PRE_HOOK }])}
        >
          <PlusIcon className="h-3.5 w-3.5 mr-1" />
          添加前置钩子
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        匹配的工具调用在 deny / allow 判定前按顺序请求外部判定服务，按返回的 True / False
        执行对应动作；「继续判定」交给下一个钩子，全部继续判定后再按 deny / allow / 默认行为处理。
      </p>
      {value.length === 0 && (
        <div className="text-sm text-muted-foreground text-center py-4 border rounded-lg border-dashed">
          无前置钩子
        </div>
      )}
      {value.map((hook, i) => (
        <div
          key={`pre-hook-${i}`}
          className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3"
        >
          <div className="flex items-center justify-between">
            <span className="text-xs font-medium text-muted-foreground">钩子 {i + 1}</span>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"
              onClick={() => onChange(value.filter((_, j) => j !== i))}
            >
              <TrashIcon className="h-4 w-4" />
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                名称 <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="如 blacklist，不可重复"
                value={hook.name}
                onChange={(e) => update(i, { name: e.target.value })}
                className="h-9 font-mono text-xs"
              />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">
                URL <span className="text-destructive">*</span>
              </Label>
              <Input
                placeholder="http://hooks.example.com/api/blacklist"
                value={hook.endpointUrl}
                onChange={(e) => update(i, { endpointUrl: e.target.value })}
                className="h-9 font-mono text-xs"
              />
            </div>
          </div>
          <ToolMatchField
            value={hook.toolMatch}
            onChange={(toolMatch) => update(i, { toolMatch })}
          />
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <ActionSelect
              label="服务返回 True 时"
              value={hook.onTrue}
              options={TOOL_PRE_HOOK_ACTION_OPTIONS}
              onChange={(onTrue) => update(i, { onTrue })}
            />
            <ActionSelect
              label="服务返回 False 时"
              value={hook.onFalse}
              options={TOOL_PRE_HOOK_ACTION_OPTIONS}
              onChange={(onFalse) => update(i, { onFalse })}
            />
            <ActionSelect
              label="服务异常时"
              value={hook.onError}
              options={TOOL_PRE_HOOK_NO_AUTO_APPROVE_OPTIONS}
              onChange={(onError) => update(i, { onError })}
            />
          </div>
          <LimitFields
            timeout={hook.timeout}
            retries={hook.retries}
            timeoutPlaceholder="默认 3"
            retriesLabel="重试次数（仅超时 / 429 / 5xx）"
            onChange={(patch) => update(i, patch)}
          />
        </div>
      ))}
    </div>
  );
}

/** AI 审批：开启后在前置钩子链末尾追加 type 与 name 均为 ai 的钩子 */
export function AiApprovalFields({
  value,
  onChange,
}: {
  value: AiApprovalFormState;
  onChange: (v: AiApprovalFormState) => void;
}) {
  const update = (patch: Partial<AiApprovalFormState>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Label htmlFor="ai_approval_enable" className="text-sm font-medium">
          AI 审批
        </Label>
        <button
          id="ai_approval_enable"
          type="button"
          role="switch"
          aria-checked={value.enabled}
          onClick={() => update({ enabled: !value.enabled })}
          className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            value.enabled ? 'bg-primary' : 'bg-muted'
          }`}
        >
          <span
            className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow ring-0 transition duration-200 ease-in-out ${
              value.enabled ? 'translate-x-5' : 'translate-x-0'
            }`}
          />
        </button>
      </div>
      <p className="text-xs text-muted-foreground">
        开启后，匹配的工具调用由智能体自身的模型按提示词判定，作为最后一个前置钩子在 HTTP
        钩子之后执行。
      </p>
      {value.enabled && (
        <div className="rounded-lg border border-primary/20 bg-primary/5 p-4 space-y-3">
          <ToolMatchField value={value.toolMatch} onChange={(toolMatch) => update({ toolMatch })} />
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">提示词</Label>
            <Textarea
              placeholder="可选，留空使用内置提示词（不含危险操作才判为 True）。例如：只允许只读的查看类命令（如 ls、cat、ps、df），任何写入、删除或向外发送数据的调用都不满足"
              value={value.prompt}
              onChange={(e) => update({ prompt: e.target.value })}
              rows={3}
              className="text-xs"
            />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <ActionSelect
              label="判定为 True 时"
              value={value.onTrue}
              options={TOOL_PRE_HOOK_ACTION_OPTIONS}
              onChange={(onTrue) => update({ onTrue })}
            />
            <ActionSelect
              label="判定为 False 时"
              value={value.onFalse}
              options={TOOL_PRE_HOOK_NO_AUTO_APPROVE_OPTIONS}
              onChange={(onFalse) => update({ onFalse })}
            />
            <ActionSelect
              label="判定失败时"
              value={value.onError}
              options={TOOL_PRE_HOOK_NO_AUTO_APPROVE_OPTIONS}
              onChange={(onError) => update({ onError })}
            />
          </div>
          <LimitFields
            timeout={value.timeout}
            retries={value.retries}
            timeoutPlaceholder="默认 30"
            retriesLabel="重试次数（超时 / 调用异常 / 未返回判定结果）"
            onChange={update}
          />
        </div>
      )}
    </div>
  );
}

export function PreHookRiskWarnings({ warnings }: { warnings: string[] }) {
  if (warnings.length === 0) return null;
  return (
    <div className="space-y-2">
      {warnings.map((warning) => (
        <div
          key={warning}
          className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-700 dark:text-amber-300"
        >
          <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{warning}</p>
        </div>
      ))}
    </div>
  );
}
